import { z } from 'zod'

/**
 * Les conditions sont le seul endroit du produit où l'on exprime « ça dépend ».
 * Elles sont volontairement pauvres : dix opérateurs, aucun calcul, aucune
 * fonction. Tout ce qui ne s'exprime pas ici doit devenir un fait calculé par
 * le serveur, jamais un opérateur de plus.
 */

export const Valeur = z.union([z.string(), z.number(), z.boolean()])
export type Valeur = z.infer<typeof Valeur>

const feuilleBase = {
  /** Chemin de fait : `demandeur.resident`, `periode.jours`, `reponses.usage`… */
  champ: z.string().min(1),
}

const Egal = z.object({ ...feuilleBase, est: Valeur }).strict()
const Different = z.object({ ...feuilleBase, pas: Valeur }).strict()
const Dans = z.object({ ...feuilleBase, dans: z.array(Valeur).min(1) }).strict()
const Min = z.object({ ...feuilleBase, min: z.union([z.number(), z.string()]) }).strict()
const Max = z.object({ ...feuilleBase, max: z.union([z.number(), z.string()]) }).strict()
/** Le fait est une liste (`periode.jours`) et contient la valeur. */
const Contient = z.object({ ...feuilleBase, contient: Valeur }).strict()
/** Le fait appartient à une liste nommée de l'organisation, modifiable en parlant. */
const DansListe = z.object({ ...feuilleBase, dans_liste: z.string().min(1) }).strict()

export type Condition =
  | z.infer<typeof Egal>
  | z.infer<typeof Different>
  | z.infer<typeof Dans>
  | z.infer<typeof Min>
  | z.infer<typeof Max>
  | z.infer<typeof Contient>
  | z.infer<typeof DansListe>
  | { tout: Condition[] }
  | { un_de: Condition[] }
  | { non: Condition }

export const Condition: z.ZodType<Condition> = z.lazy(() =>
  z.union([
    Egal,
    Different,
    Dans,
    Min,
    Max,
    Contient,
    DansListe,
    z.object({ tout: z.array(Condition).min(1) }).strict(),
    z.object({ un_de: z.array(Condition).min(1) }).strict(),
    z.object({ non: Condition }).strict(),
  ]),
)

/** Tous les chemins de faits cités par une condition, y compris `dans_liste`. */
export function faitsCites(c: Condition | undefined | null): string[] {
  if (!c) return []
  if ('tout' in c) return c.tout.flatMap(faitsCites)
  if ('un_de' in c) return c.un_de.flatMap(faitsCites)
  if ('non' in c) return faitsCites(c.non)
  return [c.champ]
}

/** Toutes les listes nommées citées par une condition. */
export function listesCitees(c: Condition | undefined | null): string[] {
  if (!c) return []
  if ('tout' in c) return c.tout.flatMap(listesCitees)
  if ('un_de' in c) return c.un_de.flatMap(listesCitees)
  if ('non' in c) return listesCitees(c.non)
  return 'dans_liste' in c ? [c.dans_liste] : []
}
